import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { FileText, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';

interface ReportInfo {
    report_key: string;
    title: string;
    description: string;
    icon?: string;
    color?: string;
}

const ReportsDashboard: React.FC = () => {
    const navigate = useNavigate();
    const level = useClientLevel();
    const [reports, setReports] = useState<ReportInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchReports = async () => {
        setIsLoading(true);
        try {
            const params: any = {};
            if (level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.get('/reports/available', { params });
            if (response.data.status) {
                setReports(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch reports', error);
            showError('Failed to load reports');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [level]);

    const getIcon = (iconName?: string, color?: string, reportKey: string = '') => {
        const IconComponent = (Icons as any)[iconName || ''] || Icons.FileText;

        const getColorClass = () => {
            if (color) {
                // Return orange for branding or specific color
                return `text-[#422568]`;
            }

            const lowKey = reportKey.toLowerCase();
            if (lowKey.includes('asset')) return 'text-blue-600';
            if (lowKey.includes('event')) return 'text-[#422568]';
            if (lowKey.includes('camera')) return 'text-emerald-600';
            if (lowKey.includes('switch') || lowKey.includes('network') || lowKey.includes('interface')) return 'text-indigo-600';
            if (lowKey.includes('poe')) return 'text-amber-600';
            if (lowKey.includes('vlan')) return 'text-purple-600';
            if (lowKey.includes('lifecycle') || lowKey.includes('warranty')) return 'text-rose-600';
            return 'text-slate-600';
        };

        return <IconComponent className={`w-4 h-4 ${getColorClass()}`} />;
    };

    const getIconBg = (_color?: string, reportKey: string = '') => {
        const lowKey = reportKey.toLowerCase();
        if (lowKey.includes('asset')) return 'bg-blue-50 border-blue-100';
        if (lowKey.includes('event')) return 'bg-[#422568]/20 border-[#422568]/20';
        if (lowKey.includes('camera')) return 'bg-emerald-50 border-emerald-100';
        if (lowKey.includes('switch') || lowKey.includes('network') || lowKey.includes('interface')) return 'bg-indigo-50 border-indigo-100';
        if (lowKey.includes('poe')) return 'bg-amber-50 border-amber-100';
        if (lowKey.includes('vlan')) return 'bg-purple-50 border-purple-100';
        if (lowKey.includes('lifecycle') || lowKey.includes('warranty')) return 'bg-rose-50 border-rose-100';
        return 'bg-slate-50 border-slate-100';
    };

    return (
        <div className="p-4 w-full min-h-screen bg-slate-50 text-slate-900 mb-20">
            {/* Header */}
            <div className="flex items-center gap-2.5 mb-6">
                <div className="bg-[#422568] p-1.5 rounded-lg">
                    <FileText className="w-4 h-4 text-white" />
                </div>
                <div>
                    <h1 className="text-[12px] font-black text-slate-900 uppercase tracking-widest leading-none">Reports Engine</h1>
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter mt-1">Analytics & Compliance Documentation</p>
                </div>
            </div>

            {/* Main Content Container */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                {/* Section Header */}
                <div className="px-4 py-2 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
                    <h2 className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Available Reports</h2>
                    <div className="px-2 py-0.5 bg-white rounded-full border border-slate-200 shadow-sm">
                        <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">{reports.length} Categories</span>
                    </div>
                </div>

                {/* List of Reports */}
                <div className="divide-y divide-slate-100">
                    {isLoading ? (
                        <div className="py-20 flex flex-col items-center justify-center space-y-3">
                            <Loader2 className="w-8 h-8 animate-spin text-[#422568]" />
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Synchronizing Report Library</p>
                        </div>
                    ) : reports.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1">
                            {reports.map((report) => (
                                <div
                                    key={report.report_key}
                                    onClick={() => navigate(`/report/${report.report_key}`)}
                                    className="group flex items-center justify-between px-4 py-3 hover:bg-slate-50 border-r border-b border-slate-100 last:border-b-0 transition-all cursor-pointer relative"
                                >
                                    <div className="flex items-center gap-3.5 flex-1 min-w-0">
                                        <div className={`p-2 rounded-xl border flex items-center justify-center shadow-sm transition-transform group-hover:scale-105 duration-300 shrink-0 ${getIconBg(report.color, report.report_key)}`}>
                                            {getIcon(report.icon, report.color, report.report_key)}
                                        </div>
                                        <div className="flex flex-col gap-0.5 min-w-0">
                                            <h3 className="text-[11px] font-black text-slate-900 group-hover:text-[#422568] transition-colors uppercase tracking-tight truncate">{report.title}</h3>
                                            <p className="text-[9px] text-slate-500 font-bold opacity-60 ">{report.description}</p>
                                        </div>
                                    </div>
                                    <div className="p-1 text-slate-300 group-hover:text-[#422568] transition-all group-hover:translate-x-0.5">
                                        <ChevronRight className="w-4 h-4" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="py-24 flex flex-col items-center justify-center space-y-3">
                            <div className="p-3 bg-slate-50 rounded-full">
                                <AlertCircle className="w-10 h-10 text-slate-200" />
                            </div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">No Reports Provisioned</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportsDashboard;
